import axios from "axios";
import { API } from "@/config";

export const api = axios.create({ baseURL: API, withCredentials: true });

const TOKEN_KEY = "ss_access_token";
export const setAccessToken = (t: string) => localStorage.setItem(TOKEN_KEY, t);
export const clearAccessToken = () => localStorage.removeItem(TOKEN_KEY);

// Attach the auth token to every request.
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// One silent refresh attempt when the short-lived access token expires.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const url: string = cfg.url || "";
    if (
      error.response?.status === 401 &&
      !cfg._retried &&
      !url.startsWith("/auth/")
    ) {
      cfg._retried = true;
      try {
        const { data } = await api.post("/auth/refresh");
        if (data?.access_token) setAccessToken(data.access_token);
        return api(cfg);
      } catch {
        clearAccessToken();
      }
    }
    return Promise.reject(error);
  },
);

export type GenStatus = "PENDING" | "GENERATING" | "READY" | "FAILED" | "STALLED";
export type ProjectStatus = "DRAFT" | "IN_PROGRESS" | "COMPLETED";
export type ReferenceType = "character" | "environment" | "style";

export type AspectRatio = "9:16" | "16:9";

export interface Project {
  id: string;
  title: string;
  logline?: string | null;
  story?: string | null;
  aspect_ratio?: AspectRatio;
  workflow_version?: string | null; // "direct_video_v3" = Direct Video Flow; null = legacy Scene Board
  status: ProjectStatus;
  created_at: string;
  updated_at: string;
}

export interface ProjectSummary extends Project {
  scene_count: number;
  reference_count: number;
  segment_count?: number;
}

export interface SceneImage {
  storage_path: string;
  content_type: string;
  filename: string;
  size_bytes: number;
  source?: "upload" | "ai";
  uploaded_at: string;
}

export interface Scene {
  id: string;
  project_id: string;
  order: number;
  title: string;
  description?: string | null;
  prompt?: string | null;
  status: GenStatus;
  keyframe_approved?: boolean;
  reference_ids?: string[];
  image?: SceneImage | null;
  image_history?: SceneImage[];
  image_generating?: { job_id: string; started_at: string } | null;
  content_updated_at?: string | null;
}

export const generateSceneImage = (sceneId: string) =>
  api.post<Scene>(`/scenes/${sceneId}/image/generate`).then((r) => r.data);
export const activateSceneImage = (sceneId: string, storage_path: string) =>
  api.post<Scene>(`/scenes/${sceneId}/image/activate`, { storage_path }).then((r) => r.data);
export const getGenerationCosts = () =>
  api.get<{ image_usd: number; video_usd: number }>("/generation-costs").then((r) => r.data);

export const uploadSceneImage = (sceneId: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post<Scene>(`/scenes/${sceneId}/image`, fd).then((r) => r.data);
};
export const sceneImageUrl = (sceneId: string, version: string) =>
  `${API}/scenes/${sceneId}/image?v=${encodeURIComponent(version)}`;

export interface Reference {
  id: string;
  project_id: string;
  name: string;
  type: ReferenceType;
  description?: string | null;
  images: string[];
  status: GenStatus;
}

// Projects
export const listProjects = () =>
  api.get<ProjectSummary[]>("/projects").then((r) => r.data);
export const getProject = (id: string) =>
  api.get<Project>(`/projects/${id}`).then((r) => r.data);
export const createProject = (data: {
  title?: string;
  logline?: string;
  story?: string;
  aspect_ratio?: AspectRatio;
}) => api.post<Project>("/projects", data).then((r) => r.data);
export const updateProject = (
  id: string,
  data: { title?: string; story?: string; aspect_ratio?: AspectRatio },
) => api.patch<Project>(`/projects/${id}`, data).then((r) => r.data);
export const deleteProject = (id: string) =>
  api.delete(`/projects/${id}`).then((r) => r.data);

// Scenes
export const listScenes = (projectId: string) =>
  api.get<Scene[]>(`/projects/${projectId}/scenes`).then((r) => r.data);
export const createScene = (
  projectId: string,
  data: { title: string; description?: string },
) => api.post<Scene>(`/projects/${projectId}/scenes`, data).then((r) => r.data);
export const deleteScene = (id: string) =>
  api.delete(`/scenes/${id}`).then((r) => r.data);
export const storyToScenes = (projectId: string) =>
  api
    .post<Scene[]>(`/projects/${projectId}/story-to-scenes`)
    .then((r) => r.data);
export const regenerateScenes = (projectId: string) =>
  api
    .post<Scene[]>(`/projects/${projectId}/regenerate-scenes`)
    .then((r) => r.data);

// References
export const listReferences = (projectId: string) =>
  api.get<Reference[]>(`/projects/${projectId}/references`).then((r) => r.data);
export const createReference = (
  projectId: string,
  data: { name: string; type: ReferenceType; description?: string },
  files: File[],
) => {
  const fd = new FormData();
  fd.append("name", data.name);
  fd.append("type", data.type);
  if (data.description) fd.append("description", data.description);
  files.forEach((f) => fd.append("files", f));
  return api.post<Reference>(`/projects/${projectId}/references`, fd).then((r) => r.data);
};
export const refImageUrl = (refId: string, idx = 0) =>
  `${API}/references/${refId}/image/${idx}`;
export const updateScene = (
  id: string,
  data: {
    title?: string;
    description?: string;
    reference_ids?: string[];
    keyframe_approved?: boolean;
    status?: GenStatus;
  },
) =>
  api.patch<Scene>(`/scenes/${id}`, data).then((r) => r.data);
export const deleteReference = (id: string) =>
  api.delete(`/references/${id}`).then((r) => r.data);

// Stub generation
export interface Keyframe {
  id: string;
  scene_id: string;
  order: number;
  image_url?: string | null;
  status: GenStatus;
  warning?: string | null;
  created_at?: string | null;
}
export const listKeyframes = (sceneId: string) =>
  api.get<Keyframe[]>(`/scenes/${sceneId}/keyframes`).then((r) => r.data);
export interface Clip {
  id: string;
  scene_id: string;
  keyframe_id?: string | null;
  video_url?: string | null;
  duration_seconds?: number | null;
  status: GenStatus;
  start_source?: "scene_image" | "keyframe" | null;
  aspect_ratio?: string | null;
  created_at?: string;
}
export const clipVideoUrl = (clipId: string, version?: string) =>
  `${API}/clips/${clipId}/video${version ? `?v=${encodeURIComponent(version)}` : ""}`;
export const generateKeyframe = (sceneId: string) =>
  api.post<Keyframe>(`/scenes/${sceneId}/generate-keyframe`).then((r) => r.data);
export const listClips = (sceneId: string) =>
  api.get<Clip[]>(`/scenes/${sceneId}/clips`).then((r) => r.data);
export const duplicateScene = (sceneId: string) =>
  api.post<Scene>(`/scenes/${sceneId}/duplicate`).then((r) => r.data);
export const moveScene = (sceneId: string, direction: "up" | "down") =>
  api.post<Scene[]>(`/scenes/${sceneId}/move?direction=${direction}`).then((r) => r.data);
export const animateScene = (sceneId: string) =>
  api.post<Clip>(`/scenes/${sceneId}/animate`).then((r) => r.data);

// Generation jobs & cost tracking
export type JobType = "STORY_TO_SCENES" | "KEYFRAME" | "ANIMATE" | "REFERENCE";
export type CostSource = "calculated" | "provider_reported";

export interface Cost {
  predicted_cost?: number | null;
  calculated_cost?: number | null;
  provider_reported_cost?: number | null;
  delta?: number | null;
  currency: string;
  cost_source: CostSource;
}

export interface GenerationJob {
  id: string;
  project_id: string;
  job_type: JobType;
  target_type?: string | null;
  target_id?: string | null;
  status: GenStatus;
  cost: Cost;
  created_at: string;
}

export interface CostSummary {
  project_id: string;
  job_count: number;
  currency: string;
  predicted_cost: number;
  calculated_cost: number;
  provider_reported_cost: number;
  effective_cost: number;
}

export const listGenerationJobs = (projectId: string) =>
  api
    .get<GenerationJob[]>(`/projects/${projectId}/generation-jobs`)
    .then((r) => r.data);
export const getCostSummary = (projectId: string) =>
  api.get<CostSummary>(`/projects/${projectId}/cost-summary`).then((r) => r.data);

// Render / Montaggio (FFmpeg — nessun costo AI)
export type Trim = { start?: number | null; end?: number | null };

export interface RenderJob {
  id: string;
  project_id: string;
  status: GenStatus;
  output_storage_path?: string | null;
  duration_seconds?: number | null;
  included_scene_ids: string[];
  included_clip_ids: string[];
  trims: Record<string, Trim>;
  error_message?: string | null;
  created_at: string;
}

export const renderProject = (
  projectId: string,
  trims: Record<string, Trim> = {},
) =>
  api
    .post<RenderJob>(`/projects/${projectId}/render`, { trims })
    .then((r) => r.data);
export const getLatestRender = (projectId: string) =>
  api
    .get<RenderJob | null>(`/projects/${projectId}/render`)
    .then((r) => r.data);
export const renderVideoUrl = (renderId: string) =>
  `${API}/renders/${renderId}/video`;
export const renderDownloadUrl = (renderId: string) =>
  `${API}/renders/${renderId}/video?download=true`;

// Simulation Studios (standalone utility — not tied to a project)
export interface VideoJob {
  job_id: string;
  status: "PREPARING" | "READY" | "FAILED";
  error?: string | null;
  filename?: string | null;
  duration_seconds?: number | null;
}

export const prepareVideo = (url: string) =>
  api
    .post<{ job_id: string; status: string }>("/simulation-studios/prepare", { url })
    .then((r) => r.data);
export const getVideoJob = (jobId: string) =>
  api.get<VideoJob>(`/simulation-studios/jobs/${jobId}`).then((r) => r.data);

// --- Auth (email+password JWT + Emergent-managed Google Auth) ---
export interface AuthUserDto {
  id: string;
  email: string;
  name?: string | null;
  picture?: string | null;
  role: string;
  providers: string[];
  credits_balance: number;
  subscription_status: string;
  subscription_plan?: string | null;
}

const storeToken = (data: any) => {
  if (data?.access_token) setAccessToken(data.access_token);
  return data;
};

export const setPreferredLanguage = (preferred_language: string) =>
  api.patch<{ preferred_language: string }>("/auth/me/language", { preferred_language }).then((r) => r.data);

export const listProviders = () =>
  api.get<{ enabled: string[] }>("/auth/providers").then((r) => r.data);
export const login = (email: string, password: string) =>
  api
    .post<{ user: AuthUserDto; access_token: string }>("/auth/login", { email, password })
    .then((r) => storeToken(r.data));
export const register = (email: string, password: string, name?: string, acceptLegal = false) =>
  api
    .post<{ user: AuthUserDto; access_token: string }>("/auth/register", { email, password, name, accept_legal: acceptLegal })
    .then((r) => storeToken(r.data));
export const LEGAL_ACCEPT_KEY = "ss_legal_accept"; // sessionStorage flag set by the checkbox before the Google redirect
export const googleSession = (sessionId: string, acceptLegal = false) =>
  api
    .post<{ user: AuthUserDto; session_token: string }>("/auth/session", { session_id: sessionId, accept_legal: acceptLegal })
    .then((r) => r.data);

// --- Legacy data (pre-accounts) — explicit claim only ---
export const legacySummary = () =>
  api
    .get<{ counts: Record<string, number>; total: number }>("/auth/legacy/summary")
    .then((r) => r.data);
export const claimLegacy = () =>
  api
    .post<{ claimed: Record<string, number>; total: number }>("/auth/legacy/claim", { confirm: true })
    .then((r) => r.data);

// --- Public statistics (real data) ---
export interface PublicStats {
  registered_users: number;
  views: number;
  videos_generated: number;
  online: number;
}

export const getPublicStats = () =>
  api.get<PublicStats>("/public/stats").then((r) => r.data);
export const heartbeat = (visitorId: string, countView = false) =>
  api
    .post<PublicStats>("/public/heartbeat", { visitor_id: visitorId, count_view: countView })
    .then((r) => r.data);
